import { progressOf, rupees } from '../state/sale'
import type { SaleEngine } from '../state/useSaleEngine'

/**
 * The right-hand panel: where the sale has got to, what is being sold, and what Cashit will pay.
 *
 * Every figure here is read from the PriceQuote that /api/v1/sell/price-quote returned. There is no
 * estimate, no placeholder amount and no arithmetic in this file - before Cashit has priced the device,
 * the panel says so rather than showing a number that would later change.
 */
export default function QuotePanel({
  engine,
  open,
  onClose,
}: {
  engine: SaleEngine
  /** Whether the drawer is showing. Closed, it is hidden from assistive technology as well as from sight. */
  open: boolean
  onClose: () => void
}) {
  const { sale, assessment } = engine
  const { percent, label } = progressOf(sale.step)
  const quote = sale.quote

  const deviceName = sale.device?.name ?? sale.categoryName ?? 'Your device'
  const caption =
    quote?.variantName ??
    assessment?.modelVariants.find((v) => v.deviceVariantId === sale.answers.deviceVariantId)?.name ??
    (sale.device ? 'Answer a few questions for your price' : "Let's find the exact model")

  const conditionSummary = (() => {
    if (!assessment || sale.answers.conditionIds.length === 0) return 'To be checked'
    const names = assessment.conditions
      .filter((c) => sale.answers.conditionIds.includes(c.id))
      .map((c) => c.conditionName)
    return names.length > 0 ? names.join(', ') : 'To be checked'
  })()

  return (
    <aside className="right" id="saleSummary" aria-label="Your sale summary" aria-hidden={!open}>
      <button type="button" className="right-close" aria-label="Close sale summary" onClick={onClose}>
        ✕
      </button>

      <div>
        <h2>Your sale, at a glance</h2>
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Sale progress"
        >
          <span style={{ width: `${percent}%` }} />
        </div>
        <div className="progress-label">{label}</div>
      </div>

      <div className="panel">
        <div className="panel-label">Device details</div>
        <div className="device-symbol" aria-hidden="true">
          ✦
        </div>
        <div className="device-name">{deviceName}</div>
        <p className="device-caption">{caption}</p>
        <div className="line">
          <span>Condition</span>
          <span>{conditionSummary}</span>
        </div>
        <div className="line">
          <span>Pickup</span>
          {/* "Not entered" read as something the customer had forgotten to do. The pickup address is
              chosen at confirmation, so until then this says when it will be asked, not that it is missing. */}
          <span>
            {sale.pincode
              ? sale.pickup?.city
                ? `${sale.pincode} · ${sale.pickup.city}`
                : sale.pincode
              : 'Chosen at confirmation'}
          </span>
        </div>
      </div>

      <div className="panel">
        <div className="panel-label">Cashit will pay</div>

        {quote?.eligibility === 'Ineligible' && <span className="flag refused">Cannot be accepted</span>}
        {quote?.requiresManualValuation && <span className="flag review">Needs a manual valuation</span>}

        <p className="price">
          {!quote
            ? 'After a few answers'
            : quote.eligibility === 'Ineligible'
              ? '—'
              : quote.requiresManualValuation
                ? 'After inspection'
                : rupees(quote.sellerPayout)}
        </p>

        <p className="fine">
          {!quote
            ? "Your price comes from Cashit's live valuation once you've answered a few questions."
            : quote.eligibility === 'Ineligible'
              ? quote.eligibilityReasons.join('; ')
              : quote.requiresManualValuation
                ? 'Cashit will value this one by hand and confirm the amount with you.'
                : 'From Cashit’s live valuation. Confirmed after inspection at pickup.'}
        </p>

        {quote && quote.couponBonus > 0 && quote.couponCode && (
          <p className="fine">
            Includes a {rupees(quote.couponBonus)} bonus for {quote.couponCode}.
          </p>
        )}
        {quote?.couponRejectedReason && <p className="fine">{quote.couponRejectedReason}</p>}
      </div>

      <div className="process">
        <strong>What happens next</strong>
        <div>
          <b>1</b> Describe your device
        </div>
        <div>
          <b>2</b> See Cashit's live price
        </div>
        <div>
          <b>3</b> Verify your number and confirm
        </div>
      </div>
    </aside>
  )
}
